<template>
<div class="row">

	<div class="col-12" v-if="!isFetching">
		<h2 class="display-4">{{ $t('sellerCost.sellerItems') }}</h2>
	</div>

	<div class="col-sm-12 mt-2" v-if="!isFetching">

		<b-button
			variant="success"
			@click="moveItems"
			v-b-tooltip.hover
			:title="$t('seller.moveItemsTooltip')"
		>
			<fa-icon icon="people-carry" />
		</b-button>

	</div>

	<div class="col-12">
		<partial-loader :loading="isFetching"></partial-loader>
	</div>

	<div class="col-12" v-if="!isFetching">

		<b-alert v-if="error"
		         variant="danger"
		         show>{{ error }}</b-alert>


		<b-table v-if="!isFetching"
				 
				striped
				hover
				show-empty
				:items="items.data"
				:fields="fields"
				:per-page="parseInt(items.per_page)"
				:sort-by.sync="sortBy"
				:sort-desc.sync="sortDesc"
				:busy="isFetching"
				:empty-text="$t('sellerCost.noItems')">

			<template v-slot:cell(number)="row">
			 	<span>{{ row.item.seller.code }}-{{ row.item.number }}</span>
			</template>

			<template v-slot:cell(price)="row">
			 	<div class="text-right" v-if="row.item.bid">&euro; {{ Math.round(row.item.bid.price) }}</div>
			</template>

			<template v-slot:cell(paid)="row">

 	 			<b-btn-group class="pull-right">

					<b-btn variant="outline-primary"
						   :to="{ name: 'item', params: { itemId: row.item.id } }"
						   v-b-tooltip.hover
						   :title="$t('item.watch')">
						<fa-icon icon="eye"
								 fixed-width />
					</b-btn>

					<b-btn v-if="row.item.paid"
						   variant="success"
						   @click="setPaid(row.item.id, 0)"
						   v-b-tooltip.hover
						   :title="$t('item.markUnpaid')">
						<fa-icon icon="check"
								 fixed-width />
					</b-btn>

					<b-btn v-if="!row.item.paid"
						   variant="outline-success"
						   @click="setPaid(row.item.id, 1)"
						   v-b-tooltip.hover
						   :title="$t('item.markPaid')">
						<fa-icon icon="check"
								 fixed-width />
					</b-btn>

 				</b-btn-group>
			</template>

		</b-table>

		<div class="row">

			<div class="col-sm-12 col-md-6">
				<p class="font-italic"
				   v-if="items.data.length > 0">
					{{ $t('item.markAll') }}
					<b-button-group size="sm">
						<b-button variant="outline-danger"
						          @click="setItemsPaymentStatus(0)">{{ $t('item.markAllUnpaid') }}</b-button>
						<b-button variant="outline-success"
						          @click="setItemsPaymentStatus(1)">{{ $t('item.markAllPaid') }}</b-button>

					</b-button-group>
				</p>
			</div>

			<div class="col-sm-12 col-md-6">
				<b-pagination-nav :link-gen="linkGen"
				                  :number-of-pages="items.last_page"
				                  :value="items.current_page"
				                  use-router
				                  align="right" />
			</div>


		</div>
	</div>

	<items-move-modal @moved="onMoved" ref="moveModal"></items-move-modal>
</div>
</template>


<script>
import { isNil } from 'lodash';
import AuctionService from '@/services/auction-service';
import ItemService from '@/services/item-service';
import PartialLoader from '@/components/elements/PartialLoader';
import ItemsMoveModal from './ItemsMoveModal';
import { flashSuccess, flashDanger } from '@/helpers/flash-message-helper';
import { isCancel, CancelToken } from 'axios';
import { smallDate } from '@/filters/date-filters';

export default {
	components: {
		PartialLoader,
		ItemsMoveModal
	},
	data() {
		return {
			isFetching: true,
			items: false,
			error: false,
			fields: [
				{ key: 'auction_order_number', label: this.$t('item.auctionOrderNumber') },
				{ key: 'number', label: this.$t('item.itemNumber') },
				{ key: 'title', label: this.$t('common.item') },
				{ key: 'price', label: this.$t('bid.price') },
				{ key: 'paid', label: this.$t('common.actions') }
			],
			currentPage: 1,
			sortBy: 'number',
			sortDesc: false,
		}
	},
	watch: {
		'$route': 'fetchBids'
	},
	methods: {
		/**
		 * Open modal for moving items to another auction
	 	 */
		moveItems() {
			this.$refs.moveModal.show();
		},

		/**
		 * Refresh items after moving
		 */
		onMoved() {
			this.fetchBids();
		},

		fetchBids() {
			this.isFetching = true;

			const query = this.$route.query;
			const currentPage = Number.parseInt(query.page);
			this.currentPage = isNaN(currentPage) ? 1 : currentPage;

			if (!isNil(this.cancelToken)) {
				this.cancelToken.cancel();
			}

			this.cancelToken = CancelToken.source();

			const params = {
				page: this.currentPage,
				sort: this.sortBy,
				order: this.sortDesc ? 'desc' : 'asc'
			}

			return AuctionService.getAuctionPersonItems(this.$route.params.auctionId, this.$route.params.personId, params, this.cancelToken.token).then(response => {
				this.isFetching = false;
				this.items = response;
			}).catch(error => {
				if (!isCancel(error)) {
					this.isFetching = false;
					this.error = error.message;
				}
			});
		},
		setPaid(itemId, paid) {

			this.isFetching = true;

			return ItemService.updateOne(itemId, { paid: paid }).then(response => {
				this.isFetching = false;
				if (paid) {
					flashSuccess(this.$t('item.paidSuccess'));
				} else {
					flashSuccess(this.$t('item.unPaidSuccess'));
				}
				this.fetchBids();
			}).catch(error => {
				if (!isCancel(error)) {
					this.isFetching = false;
					this.error = error.message;
				}
			});
		},
		setItemsPaymentStatus(paid) {
			this.isFetching = true;

			return ItemService.setSellerItemsPaymentStatus(this.$route.params.auctionId, this.$route.params.personId, { paid: paid }).then(response => {
				this.isFetching = false;
				if (paid) {
					flashSuccess(this.$t('item.allItemsPaidSuccess'));
				} else {
					flashSuccess(this.$t('item.allItemsUnpaidSuccess'));
				}
				this.fetchBids();
			}).catch(error => {
				if (!isCancel(error)) {
					this.isFetching = false;
					this.error = error.message;
				}
			});
		},
		linkGen(pageNumber) {
			return `?page=${pageNumber}`;
		}
	},
	mounted() {
		this.fetchBids();
	}
}
</script>

<style lang="scss" scoped>

</style>



// WEBPACK FOOTER //
// ItemList.vue?4ed604b6